import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter} from '@angular/core';
import {BaseComponent} from '../../../general/base/base.component';
import {VivoService} from '../../../../vivo/vivo.service';
import {Asset, VivoNotification, VivoPosition, VivoPriority, VivoSubscriber, VivoParent} from './vivo-notification.model';
import {forEach, groupBy, remove} from 'lodash-es';
import {GoogleProviderService} from '../../../../shared/google-provider.service';
import { _ } from '../../../../shared/utils';
import { Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { MapService } from '../../../../shared/map/map.service';
import { MapPlace } from '../../../../shared/map/types';

@Component({
  selector: 'qm-vivo-notification',
  templateUrl: './vivo-notification.component.html',
  styleUrls: ['./vivo-notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VivoNotificationComponent extends BaseComponent<VivoNotification> implements OnInit, OnChanges {
  @Input()
  public edit: VivoNotification;

  @Output()
  public close = new EventEmitter<void>();

  // set in ngOnInit / ngOnChange
  public vivoNotification: VivoNotification;

  public users: VivoSubscriber[] = [];
  public vivoPrioritys: VivoPriority[] = [];

  public vivoResults: { id: string, name: string, holder: string }[] = [];
  public query: string;
  public addQuery: string;
  public model: string;

  public showAutocomplete: boolean = false;
  public addressResults: MapPlace[] = [];

  public search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      map(term => this.users.filter(v => v.name.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10)))
  public formatter = (x: {name: string}) => x.name;

  /**
   *
   * @param {VivoService} vivoService
   * @param {SearchService} searchService
   * @param {ChangeDetectorRef} changeDetector
   */
  constructor(private vivoService: VivoService,
              private changeDetector: ChangeDetectorRef,
              private google: GoogleProviderService,
              private mapService: MapService) {
    super();
    this.name = 'VivoNotificationComponent';
  }

  ngOnInit() {
    this.onEditChanged();
    this.preparations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.edit) {
      this.onEditChanged();
    }
  }

  onEditChanged(): void {
    if (this.edit) {
      this.vivoNotification = new VivoNotification(this.edit);
    } else {
      this.vivoNotification = new VivoNotification({
        actions: [{ template: this.vivoService.getDiaryTaskType() }]
      });
    }
    this.changeDetector.markForCheck();
  }

  public onFocus(e: Event): void {
    e.stopPropagation();
    setTimeout(() => {
      const inputEvent: Event = new Event('input');
      e.target.dispatchEvent(inputEvent);
    }, 0);
  }

  public async preparations() {
    const [usersResponse, prioritysResponse] = await Promise.all([
      this.vivoService.getVivoUsers(),
      this.vivoService.vivoTaskPrioritys()
    ]);
    forEach(usersResponse, o => {
      this.users.push({
        holderID: o._id,
        name: o.firstName + ' ' + o.lastName,
        type: 'user'
      });
    });
    forEach(prioritysResponse, o => {
      this.vivoPrioritys.push(o);
    });
    this.changeDetector.markForCheck();
  }

  public async doSearch() {
    if (this.query && this.query.length >= 2) {
      const results = await this.vivoService.vivoSearch(this.query);
      if (results.docs && results.docs.length > 0) {
        this.vivoResults = [];
        forEach(results.docs, (o: any) => {
          this.vivoResults.push({id: o._id, name: o.name, holder: o.holder.name});
        });
      }
    }
    this.changeDetector.markForCheck();
  }

  public comparePriorities(a: any, b: any): boolean {
    return a === b || a?._id === b?._id;
  }

  async doAddressSearch() {
    this.addressResults = [];
    if (this.addQuery && this.addQuery.length > 1) {
      const mapPlaces = await this.mapService.searchResults(this.addQuery, 'fi');
      this.addressResults = mapPlaces;
      this.changeDetector.markForCheck();
    }
  }

  resetAddress() {
    this.addressResults = [];
    this.addQuery = '';
  }

  async selectAddress(selection: MapPlace) {
    // Doesn't work with google's MapService variant (autocomplete doesn't return coordinates)
    if (selection.geometry.type === 'Point') {
      const poi: VivoPosition = {
        type: 'POI',
        state: _('pending'),
        name: selection.description,
        geo: {
          type: 'point',
          coordinates: selection.geometry.coordinates
        }
      };
      remove(this.vivoNotification.parents, parent => parent.type !== poi.type);
      this.vivoNotification.parents.push(poi);
      this.resetAddress();
    }
  }

  public assignAsset(single: any) {
    this.query = null;
    this.vivoResults = [];
    const testi = groupBy(this.vivoNotification.parents, 'name');
    const result = testi[single.name];
    if (!result) {
      const selectedAsset: Asset = {
        id: single.id,
        type: 'Asset',
        name: single.name,
        state: 'pending',
        asset: {
          _id: single.id,
          name: single.name,
        }
      };
      this.vivoNotification.parents.push(selectedAsset);
    }
    this.changeDetector.markForCheck();
  }

  public removeAsset(single: VivoParent) {
    remove(this.vivoNotification.parents, single);
  }

  public async typeAheadEvent(e: any) {
    const testi = groupBy(this.vivoNotification.subscribers, 'holderID');
    const result = testi[e.item.holderID];
    if (!result) {
      this.vivoNotification.subscribers.push(e.item);
    }
    this.model = null;
  }

  public removeUser(e: Event, user: VivoSubscriber) {
    remove(this.vivoNotification.subscribers, user);
  }

  public async save() {
    if (!this.vivoNotification.message) {
      return;
    }

    if (this.vivoNotification.id) {
      await this.vivoService.saveEdit(this.vivoNotification);
    } else {
      await this.vivoService.createVivoNotification({ task: this.vivoNotification });
    }
    this.close.emit();
  }

}
